import React, { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { forumHelpers } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';

interface SaveButtonProps {
  topicId: string;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

const SaveButton: React.FC<SaveButtonProps> = ({ 
  topicId, 
  size = 'md',
  showText = false,
  className = ''
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Se não há usuário, não renderizar o botão
  if (!user) {
    return null;
  }

  // Verificar se o tópico está salvo ao carregar
  useEffect(() => {
    if (user) {
      checkIfSaved();
    }
  }, [user?.id, topicId]);

  const checkIfSaved = async () => {
    try {
      if (!forumHelpers?.savedTopics?.isSaved) {
        console.error('forumHelpers.savedTopics.isSaved não está disponível');
        return;
      }
      const saved = await forumHelpers.savedTopics.isSaved(topicId);
      setIsSaved(saved);
    } catch (error) {
      console.error('Erro ao verificar se está salvo:', error);
    }
  };

  const handleSaveToggle = async () => {
    if (!user) {
      showToast('Faça login para salvar discussões', 'warning');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    try {
      if (!forumHelpers?.savedTopics) {
        showToast('Funcionalidade de salvamento não disponível', 'error');
        return;
      }
      
      if (isSaved) {
        // Remover dos salvos
        const result = await forumHelpers.savedTopics.unsave(topicId);
        if (result.success) {
          setIsSaved(false);
          showToast('Removido dos salvos', 'success');
        } else {
          showToast(result.message, 'error');
        }
      } else {
        // Salvar
        const result = await forumHelpers.savedTopics.save(topicId);
        if (result.success) {
          setIsSaved(true);
          showToast('Salvo com sucesso!', 'success');
        } else {
          showToast(result.message, 'error');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar/remover tópico:', error);
      showToast('Erro ao processar ação', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  };



  return (
    <button
      onClick={handleSaveToggle}
      disabled={isLoading}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200
        ${isSaved 
          ? 'bg-[#0FFF55]/20 text-[#0FFF55] hover:bg-[#0FFF55]/30' 
          : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/50 hover:text-white'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      <div className={`${sizeClasses[size]} flex items-center justify-center`}>
        {isLoading ? (
          <div className={`w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin`} />
        ) : isSaved ? (
          <BookmarkCheck size={iconSizes[size]} />
        ) : (
          <Bookmark size={iconSizes[size]} />
        )}
      </div>
      {showText && (
        <span className="text-sm font-medium">
          {isSaved ? 'Salvo' : 'Salvar'}
        </span>
      )}
    </button>
  );
};

export default SaveButton; 